import cx from 'classnames'
import {InputProvider} from 'components/common/CartItemBuilder/providers/InputProvider'
import LocalisedDate from 'components/common/LocalisedDate'
import LocalisedTime from 'components/common/LocalisedTime'
import {ReactComponent as ClockIcon} from 'icons/clock.svg'
import {EnvironmentProvider} from 'providers/EnvironmentProvider'
import {formatAvailabilityId} from 'utils/formatAvailabilityId'
import {VentrataDataProvider} from '../../../providers/VentrataDataProvider'
import {Total} from './Total'
import {Units} from './Units'

type Props = {
  className?: string
}

export const Preview = ({className}: Props) => {
  const product = VentrataDataProvider.useProduct('optional')
  const currency = EnvironmentProvider.useCurrency()
  const optionId = InputProvider.useOptionId('optional')
  const date = InputProvider.useDate('optional')
  const availabilityId = InputProvider.useAvailabilityId('optional')
  const counters = InputProvider.useUnitCounters('optional')
  const units = VentrataDataProvider.useUnits('optional')
  const availabilities = VentrataDataProvider.useAvailabilities('optional')

  const usedUnits = units.filter((unit) => unit.id in counters)

  const option = product.options.find((option) => option.id === optionId)
  const optionAvailabilities = availabilities ? availabilities[optionId] || [] : []
  const availability = optionAvailabilities.find((availability) => availability.id === availabilityId)

  return (
    <div className={cx('flex flex-col w-full', className)}>
      <div className="flex flex-col">
        {!product.bannerImageUrl && <div className="rounded-t-lg bg-gray-3 w-full h-[128px]" />}
        {product.bannerImageUrl && (
          <div
            className="rounded-t-lg bg-gray-3 w-full h-[128px]"
            style={{
              backgroundImage: `url(${product.bannerImageUrl})`,
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        )}
      </div>
      <div
        className={cx(
          'px-4 flex flex-col divide-solid divide-y divide-gray-3 bg-white rounded-b-lg',
          product.bannerImageUrl ? '' : 'rounded-t-lg'
        )}
      >
        {product.title && (
          <div className="flex flex-col py-4">
            <h2 className="font-bold">{product.title}</h2>
          </div>
        )}

        {usedUnits.length !== 0 && (
          <Units
            product={product}
            usedUnits={usedUnits}
            counters={counters}
            availability={availability}
            currency={currency}
          />
        )}

        {date && (
          <div className="flex flex-col py-4 gap-2">
            <h3 className="uppercase text-gray-500 text-xs">When</h3>
            <div className="flex gap-2">
              <div>
                <LocalisedDate date={date} />
              </div>
              <div>{availabilityId && '•'}</div>
              <div>
                <LocalisedTime date={formatAvailabilityId(availabilityId)} />
              </div>
            </div>
          </div>
        )}

        {option && (
          <div className="flex flex-col py-4">
            <h3 className="uppercase text-gray-500 text-xs mb-2">Option</h3>
            <div className="font-bold">{option.title}</div>
            <div className="flex items-center text-sm text-gray-400">
              <ClockIcon className="mr-1" />
              <span>{option.duration}</span>
            </div>
          </div>
        )}

        <Total
          product={product}
          availability={availability}
          usedUnits={usedUnits}
          counters={counters}
          currency={currency}
        />
      </div>
    </div>
  )
}
